import { useEffect, useState } from 'react';
import HERO, { HERO_MS } from '../content/hero';
import Button from './Button';
import useReducedMotion from '../hooks/useReducedMotion';

const pad2 = n => String(n).padStart(2, '0');
const TOTAL = HERO.length;

export default function HeroSlider() {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false); // drives the progress bar and slow zoom
  const [paused, setPaused] = useState(false);
  const autoplay = !reduced && !paused;

  // Restart the cycle whenever the slide changes or autoplay toggles.
  useEffect(() => {
    if (!autoplay) { setPlaying(false); return undefined; }
    setPlaying(false);
    let raf2;
    const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => setPlaying(true)); });
    const timer = setTimeout(() => setIndex(i => (i + 1) % TOTAL), HERO_MS);
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); clearTimeout(timer); };
  }, [index, autoplay]);

  const pause = () => setPaused(true);
  const resume = () => setPaused(false);

  return (
    <section
      id="top" aria-roledescription="carousel" aria-label="Introduction"
      onMouseEnter={pause} onMouseLeave={resume} onFocus={pause} onBlur={resume}
      className="relative flex flex-col overflow-hidden border-b-2 border-ink bg-ink text-bg"
      style={{ minHeight: 'clamp(620px, calc(100svh - 68px), 900px)' }}
    >
      {/* Background photos: cross-fade + slow zoom on the active slide */}
      {HERO.map((s, k) => {
        const on = k === index;
        return (
          <div key={s.label} aria-hidden="true" className="absolute inset-0 transition-opacity duration-1000" style={{ opacity: on ? 1 : 0 }}>
            <div className="bw-strong absolute inset-0" style={{ transform: on && playing ? 'scale(1.08)' : 'scale(1)', transition: `transform ${HERO_MS}ms linear` }}>
              <img src={s.image} alt={s.alt} className="h-full w-full object-cover" loading={k === 0 ? 'eager' : 'lazy'} fetchpriority={k === 0 ? 'high' : undefined} />
            </div>
          </div>
        );
      })}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(32,30,29,.88) 0%, rgba(32,30,29,.62) 48%, rgba(32,30,29,.25) 100%)' }} />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 mx-auto max-w-site px-gutter">
        <div className="h-full [--cols:4] nav:[--cols:12]" style={{ backgroundImage: 'repeating-linear-gradient(90deg, rgba(243,242,242,.08) 0 1px, transparent 1px calc(100% / var(--cols)))' }} />
      </div>

      {/* Copy */}
      <div className="container-site relative flex flex-1 items-end pb-[clamp(40px,5vw,64px)] pt-[clamp(48px,7vw,96px)]">
        <div className="grid w-full">
          {HERO.map((s, k) => {
            const on = k === index;
            const long = s.title.length > 50;
            const headingProps = k === 0 ? {} : { role: 'heading', 'aria-level': 2 };
            const Title = k === 0 ? 'h1' : 'p';
            return (
              <div
                key={s.label} role="group" aria-roledescription="slide" aria-label={`${k + 1} of ${TOTAL}: ${s.label}`} aria-hidden={!on}
                className="self-end [grid-area:1/1]"
                style={{ opacity: on ? 1 : 0, transform: on ? 'none' : 'translateY(18px)', visibility: on ? 'visible' : 'hidden', transition: 'opacity .6s ease, transform .8s cubic-bezier(.2,.8,.2,1), visibility .6s' }}
              >
                <div className="eyebrow">{s.eyebrow}</div>
                <Title {...headingProps} className={`balance mt-[22px] max-w-[1060px] ${long ? 'text-display-long' : 'text-display'}`}>{s.title}</Title>
                <p className="pretty mt-[26px] max-w-[540px] text-lead text-neutral-200">{s.sub}</p>
                <div className="mt-8 flex flex-wrap gap-2.5">
                  <Button href="#contact" variant="accent-hero" size={58} spread className="min-w-[260px]" tabIndex={on ? 0 : -1}>Book a free consultation</Button>
                  <Button href={s.href} variant="outline-light" size={58} arrow={false} tabIndex={on ? 0 : -1}>{s.cta}</Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom tab bar */}
      <div className="relative border-t-2 border-rule-light bg-ink/[0.55] backdrop-blur-[8px]">
        <div className="container-site flex items-stretch">
          <div className="flex flex-none items-center gap-2.5 pr-6 text-[15px] font-extrabold">
            <span>{pad2(index + 1)}</span>
            <span aria-hidden="true" className="h-[2px] w-6 bg-neutral-500" />
            <span className="text-neutral-400">{pad2(TOTAL)}</span>
          </div>
          <div role="tablist" aria-label="Choose slide" className="grid flex-1" style={{ gridTemplateColumns: `repeat(${TOTAL}, minmax(0,1fr))` }}>
            {HERO.map((s, k) => {
              const on = k === index;
              const fill = k < index ? '100%' : on ? ((playing || !autoplay) ? '100%' : '0%') : '0%';
              return (
                <button
                  key={s.label} type="button" role="tab" aria-selected={on} aria-label={`${k + 1} of ${TOTAL}: ${s.label}`} onClick={() => setIndex(k)}
                  className={`relative min-h-[64px] border-l-2 border-rule-light-soft px-4 text-left text-[15px] font-semibold transition-colors hover:bg-bg/[0.06] hover:text-bg ${on ? 'text-bg' : 'text-neutral-400'}`}
                >
                  <span aria-hidden="true" className="absolute -left-[2px] -top-[2px] right-0 h-[2px]">
                    <span className="block h-full bg-accent" style={{ width: fill, transition: on && playing ? `width ${HERO_MS}ms linear` : 'none' }} />
                  </span>
                  <span className="hidden nav:inline">{s.label}</span>
                  <span className="nav:hidden">{pad2(k + 1)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
